/**
 * This custom hook will select which i18n instance to use, as well
 * as provide a translation function that allows interpolating
 * components.
 *
 * @NOTE I can't seem to figure out how to get it to automatically use
 * the right context so at the moment this hook provides the normal `t`
 * function from `react-i18next`, as well as a `superT` function which
 * can interpolate react elements/nodes.
 */
import React from "react";
import i18next from "i18next";
import {
  Namespace,
  UseTranslationOptions,
  useTranslation
} from "react-i18next";
import set from "lodash/set";
import { get, isObject } from "../utils/helpers";
import appI18n from "../utils/i18n";

export type SuperTResult = React.ReactNode | React.ReactNodeArray | undefined;

export interface SuperTInterpolationMap {
  [keyword: string]: React.ReactNode | React.ReactNodeArray | any;
}

export interface SuperTFunction<
  TResult extends SuperTResult = SuperTResult,
  TKeys extends string | TemplateStringsArray = string,
  TInterpolationMap extends object = SuperTInterpolationMap
> {
  (
    key: TKeys | TKeys[],
    options?: i18next.TOptions<TInterpolationMap> | string
  ): TResult;
}

export type TFunction = i18next.TFunction | SuperTFunction<React.ReactNode>;

// @NOTE if ever want to mimic the useTranslation array destructuring
// export type UseT = [TFunction, i18next.i18n, boolean] & {
export interface UseT {
  t: i18next.TFunction;
  superT: TFunction;
  i18n: i18next.i18n;
  ready: boolean;
}

const ELEMENT_DELIMITER = "§§";
const RE_NUMBERED_REACT_ELEMENT = /^_reactElement:(\d+)_$/i;

/**
 * Curry a regular react-i18next T function with some extra super powers,
 * such as interpolating keywords with React components, and also automatically
 * inserting line breaks where new lines are.
 */
export function createSuperTFn(t: i18next.TFunction): SuperTFunction {
  const superT: SuperTFunction = (key, options) => {
    // Add a {br} key for automatic <br /> component interpolation
    const _options: SuperTInterpolationMap = Object.assign(
      { br: <br /> },
      isObject(options) ? options : undefined
    );
    let brIndex = 0;

    // Check if any react elements are provided as interpolation values and
    // add them to an array to interpolate later
    const reactElements: React.ReactElement[] = [];
    const newOptions: SuperTInterpolationMap = _options
      ? Object.keys(_options).reduce((acc, optionName) => {
          const optionValue = get(_options, optionName);

          // Put the React components/elements into an array and put in a special interpolation key
          if (React.isValidElement(optionValue)) {
            const elementIndex = reactElements.push(optionValue) - 1;

            // Set the brIndex for later replacing
            if (optionName === "br") {
              brIndex = elementIndex;
            }

            // I'm creating an interpolation key with a unique format to
            // replace with the react element later
            set(
              acc,
              optionName,
              `${ELEMENT_DELIMITER}_reactElement:${elementIndex}_${ELEMENT_DELIMITER}`
            );
          } else {
            set(acc, optionName, optionValue);
          }
          return acc;
        }, {})
      : {};

    // Replace the placeholder interpolation keys with the react elements
    if (reactElements.length) {
      // Get the translated text using the new options
      let translatedText = t(key, newOptions);

      // Replace any detected new line characters with the line break react element
      translatedText = translatedText
        .trim()
        .replace(
          /\r?\n/g,
          `${ELEMENT_DELIMITER}_reactElement:${brIndex}_${ELEMENT_DELIMITER}`
        );

      // Replace the react element placeholders with the actual elements themselves
      const splitNumberedElements = translatedText
        .split(ELEMENT_DELIMITER)
        .filter(n => n);
      const countNumberedElements = splitNumberedElements.filter(n =>
        RE_NUMBERED_REACT_ELEMENT.test(n)
      );

      // No need to process numbered nodes if there aren't any
      if (!countNumberedElements.length) {
        return translatedText;
      }

      const output = splitNumberedElements.map((input, index) => {
        // Replace the unique placeholder with the react element, pulling
        // from the placeholder the index of the react element in the
        // reactElements array
        if (RE_NUMBERED_REACT_ELEMENT.test(input)) {
          const elementIndex = parseInt(
            input.replace(RE_NUMBERED_REACT_ELEMENT, "$1"),
            10
          );

          // Clone the element to add a new key. Any existing keys on the
          // element will be replaced with this one
          const element = React.cloneElement(reactElements[elementIndex], {
            ...reactElements[elementIndex].props,
            key: `${input} ${index}`
          });

          return element;
        } else {
          return input;
        }
      });

      return output;
    }
    // Otherwise translate as usual
    else {
      return t(key, options);
    }
  };

  return superT;
}

export default function useT(
  ns?: Namespace,
  options?: UseTranslationOptions
  // initOptions?: i18next.InitOptions
): UseT {
  // const [appState] = useContext(AppStateContext);
  // const [customerPage] = useContext(CustomerPageContext);
  // const language = has(customerPage, "language")
  //   ? customerPage.language
  //   : appState.language;

  // // Figure out which i18n instance to use
  // const getI18n = useMemo(() => {
  //   // If the customerPage context is visible to the component, use the
  //   // customer page i18n instance.
  //   if (customerPage && customerPage.language !== appState.language) {
  //     if (has(I18N_INSTANCES, "customer")) {
  //       debug("use existing customer i18n instance");
  //       return I18N_INSTANCES.customer;
  //     } else {
  //       I18N_INSTANCES["customer"] = createI18nInstance({
  //         lng: customerPage.language,
  //         ...initOptions
  //       });

  //       debug("create customer i18n instance");
  //       return I18N_INSTANCES["customer"];
  //     }
  //   }
  //   // Use the default i18n instance
  //   else {
  //     debug("use default i18n instance");
  //     return appI18n;
  //   }
  // }, [language]);

  // Get the normal t function and the i18n instance
  const { t, i18n, ready } = useTranslation(ns, {
    i18n: appI18n, // getI18n,
    ...options
  });

  // Supercharge the t function
  const superT: SuperTFunction = createSuperTFn(t);

  return { t, superT, i18n, ready };
}
